import { NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom'
import { banSearch, banText, readBans } from '../lib/bans'
import { scaleSearch } from '../lib/scales'

const RECORD_LINKS = [
  { to: '/seasons', label: 'Seasons' },
  { to: '/drivers', label: 'Drivers' },
  { to: '/constructors', label: 'Constructors' },
]

const F2_LINKS = [
  { to: '/f2/championship', label: 'Championship' },
  { to: '/f2/drivers', label: 'Drivers' },
  { to: '/f2/season', label: 'Season' },
]

const F15_LINKS = [
  { to: '/f15/championship', label: 'Championship' },
  { to: '/f15/drivers', label: 'Drivers' },
  { to: '/f15/season', label: 'Season' },
]

const REVERSE_LINKS = [
  { to: '/reverse/championship', label: 'Championship' },
  { to: '/reverse/drivers', label: 'Drivers' },
  { to: '/reverse/season', label: 'Season' },
]

const BAN_LINKS = [
  { to: '/ban', label: 'Bans' },
  { to: '/ban/championship', label: 'Championship' },
  { to: '/ban/drivers', label: 'Drivers' },
  { to: '/ban/constructors', label: 'Constructors' },
  { to: '/ban/season', label: 'Season' },
]

const SCALE_LINKS = [
  { to: '/points', label: 'Points table' },
  { to: '/points/championship', label: 'Championship' },
  { to: '/points/drivers', label: 'Drivers' },
  { to: '/points/constructors', label: 'Constructors' },
  { to: '/points/season', label: 'Season' },
]

const QUALIFYING_LINKS = [
  { to: '/qualifying/championship', label: 'Championship' },
  { to: '/qualifying/drivers', label: 'Drivers' },
  { to: '/qualifying/constructors', label: 'Constructors' },
  { to: '/qualifying/season', label: 'Season' },
]

const PRACTICE_LINKS = [
  { to: '/fp/championship', label: 'Championship' },
  { to: '/fp/drivers', label: 'Drivers' },
  { to: '/fp/constructors', label: 'Constructors' },
  { to: '/fp/season', label: 'Season' },
]

export function Layout() {
  const [params] = useSearchParams()
  // The bans and the points table travel with every link of their menu, so
  // moving between its pages never loses the selection.
  const bans = banSearch(params)
  const scale = scaleSearch(params)
  return (
    <div className="shell">
      <header className="shell__bar">
        <a className="shell__brand" href="/">
          What if<span className="shell__brand-mark">?</span>
        </a>
        <nav className="shell__nav">
          <NavLink to="/" end>
            Home
          </NavLink>
          <Menu
            label="As it happened"
            prefix={['/seasons', '/races', '/drivers', '/constructors']}
            links={RECORD_LINKS}
          />
          <Menu label="F2 rules" prefix="/f2/" links={F2_LINKS} />
          <Menu label="F1.5 rules" prefix="/f15/" links={F15_LINKS} />
          <Menu label="Reverse" prefix="/reverse/" links={REVERSE_LINKS} />
          <Menu label="With Ban" prefix="/ban" links={BAN_LINKS} search={bans} />
          <Menu label="Custom Points" prefix="/points" links={SCALE_LINKS} search={scale} />
          <Menu label="Qualifying" prefix="/qualifying/" links={QUALIFYING_LINKS} />
          <Menu label="FP Championship" prefix="/fp/" links={PRACTICE_LINKS} />
          <NavLink to={`/compare${compareSearch(params)}`}>Compare</NavLink>
        </nav>
      </header>
      <main className="shell__main">
        <Outlet />
      </main>
      <footer className="shell__foot">
        Results from <a href="https://github.com/f1db/f1db">f1db</a>. Championships recomputed
        from the published rules of each season.
      </footer>
    </div>
  )
}

/**
 * The bans and the points table being read, set against the record: the
 * table becomes side B's, or else the bans do.
 */
function compareSearch(params: URLSearchParams): string {
  const next = new URLSearchParams()
  const bans = banText(readBans(params))
  const pts = params.get('pts')
  if (bans) next.set('bb', bans)
  if (pts) next.set('pb', pts)
  if (pts || next.size) next.set('b', pts ? 'points' : 'ban')
  return next.size ? `?${next}` : ''
}

/**
 * A category whose pages open on hover and on keyboard focus alike:
 * `:focus-within` keeps it open while tabbing through its links.
 */
function Menu({
  label,
  prefix,
  links,
  search = '',
}: {
  label: string
  prefix: string | string[]
  links: { to: string; label: string }[]
  search?: string
}) {
  const { pathname } = useLocation()
  return (
    <div className="menu">
      <NavLink
        to={links[0]!.to + search}
        className={() =>
          [prefix].flat().some((p) => pathname.startsWith(p)) ? 'active' : undefined
        }
        aria-haspopup="true"
      >
        {label} <span aria-hidden="true">▾</span>
      </NavLink>
      {/* Blurred on choosing, or `:focus-within` would hold it open over
          the page just opened. */}
      <div
        className="menu__panel"
        onClick={() => (document.activeElement as HTMLElement | null)?.blur()}
      >
        {links.map((link) => (
          <NavLink key={link.to} to={link.to + search} end>
            {link.label}
          </NavLink>
        ))}
      </div>
    </div>
  )
}
